import { IMAGES } from "./images";

/** DigiRobot legacy brand colors (logo indigo + hero orange/blue) */
export const BRAND_COLORS = {
  primary: "#3a00e0",
  primarySoft: "#4400e8",
  primaryBright: "#5b21ff",
  /** Accent chaud — alias explicite de `accent` (#fab03a) */
  orange: "#fab03a",
  accent: "#fab03a",
  /** Orange plus saturé (hovers / fills chauds) */
  accentHot: "#f97316",
  /** Orange texte sur fond clair (meilleur contraste que #fab03a) */
  orangeInk: "#c47a0a",
  navy: "#18449e",
  navyDeep: "#22266a",
};

export const DEFAULT_BRAND = {
  brand_name: process.env.REACT_APP_BRAND_NAME || "DigiRobot",
  brand_short: process.env.REACT_APP_BRAND_SHORT || "DR",
  logo_url: IMAGES.logo,
  logo_long_url: IMAGES.logoLong,
  hero_image_url: IMAGES.heroAtelier,
  hero_title: "Robotique, Programmation & IA dès 6 ans",
  hero_subtitle:
    "Ateliers, stages et formations STEM pour préparer vos enfants au monde de demain avec DigiRobot.",
  seo_description:
    "DigiRobot — Ateliers, cursus et stages de robotique et programmation pour enfants dès 6 ans sur la Côte d'Azur (Nice, Cannes, Monaco).",
  contact_email: "contact@digirobot.fr",
  contact_phone: "06 98 85 49 48",
  contact_address: "Nice, Cannes, Monaco, Pégomas et Cagnes-sur-Mer",
  social_facebook: "https://www.facebook.com/profile.php?id=100087755246254",
  social_instagram: "https://www.instagram.com/digirobot_/",
  social_linkedin: "",
  social_youtube: "",
  booking_center_step_enabled: false,
};

export const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.digirobot.fr";

/** Merge API settings over defaults ; empty strings keep the fallback. */
export function mergeBrandSettings(data = {}) {
  const merged = { ...DEFAULT_BRAND };
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    merged[key] = typeof value === "string" ? value.trim() : value;
  }
  return merged;
}

export function mediaSrc(pathOrUrl) {
  if (!pathOrUrl) return "";
  const value = String(pathOrUrl).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  if (path.startsWith("/api/")) {
    const base = String(BACKEND_URL || "").replace(/\/$/, "");
    return `${base}${path}`;
  }
  return path;
}

export function absoluteAssetUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  if (path.startsWith("/api/")) {
    const base = String(BACKEND_URL || SITE_URL).replace(/\/$/, "");
    return `${base}${path}`;
  }
  return `${SITE_URL}${path}`;
}

/** Surfaces marketing (cards, popups, bandes, header pill) — 1.25rem */
export const RADIUS_SURFACE = "rounded-2xl";
/** Pills / CTAs / chips — capsule, pas le rayon surface */
export const RADIUS_PILL = "rounded-full";

/** Primary / secondary button classes used across public pages */
export const BTN_PRIMARY =
  `${RADIUS_PILL} text-white font-semibold bg-gradient-to-r from-[#3a00e0] via-[#5b21ff] to-[#fab03a] bg-[length:200%_auto] bg-left hover:bg-right shadow-lg shadow-[0_10px_28px_-8px_rgba(58,0,224,0.35)] hover:shadow-xl hover:shadow-orange-500/25 hover:-translate-y-0.5 transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3a00e0] focus-visible:ring-offset-2`;
/** CTA secondaire — bordure neutre, hover avec rappel orange (pas indigo) */
export const BTN_OUTLINE =
  `${RADIUS_PILL} text-slate-800 font-medium border border-slate-300 bg-white hover:bg-[rgba(250,176,58,0.08)] hover:border-[rgba(250,176,58,0.65)] hover:text-[#c47a0a] hover:-translate-y-0.5 shadow-sm hover:shadow-md hover:shadow-[0_8px_24px_-10px_rgba(250,176,58,0.35)] transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3a00e0] focus-visible:ring-offset-2`;

/** Admin clair — champs (même langage que le site public) */
export const ADMIN_FIELD =
  "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 mt-1 focus-visible:ring-[#3a00e0]/20 focus-visible:border-[#3a00e0]/40";
/** Admin clair — surface section / dialog */
export const ADMIN_PANEL = "bg-white border-slate-200";
/** Admin clair — labels de formulaire */
export const ADMIN_LABEL = "text-slate-700";
/** Admin clair — <select> natif */
export const ADMIN_SELECT =
  "w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3a00e0]/20 focus-visible:border-[#3a00e0]/40";
/** Admin clair — chips filtres / onglets */
export const ADMIN_CHIP =
  "px-3.5 py-1.5 rounded-full text-sm transition-colors text-slate-600 hover:bg-slate-100";
export const ADMIN_CHIP_ACTIVE =
  "px-3.5 py-1.5 rounded-full text-sm font-medium bg-[rgba(58,0,224,0.08)] text-[#3a00e0] ring-1 ring-inset ring-[rgba(58,0,224,0.16)]";
export const ADMIN_BADGE_OK = "bg-emerald-50 text-emerald-700";
export const ADMIN_BADGE_OFF = "bg-slate-100 text-slate-500";
export const ADMIN_BADGE_WARN = "bg-amber-50 text-amber-700";
export const ADMIN_BADGE_PROMO = "bg-[rgba(58,0,224,0.08)] text-[#3a00e0]";

/**
 * Base URL de l'API.
 * - URL absolue (dev Node : http://localhost:8000)
 * - "same" ou "" : même origine (Docker/nginx proxy /api — idéal tunnel démo)
 */
function resolveBackendUrl() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw === "same" || raw === "") {
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "";
  }
  return raw;
}

export const BACKEND_URL = resolveBackendUrl();
